import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Calendar,
  Target,
  MapPin,
  Clock,
  Users,
  DollarSign,
  Building,
  Search,
  Filter,
  User,
  FileText,
  CheckCircle,
  XCircle,
  ChevronRight,
  AlertTriangle,
  BadgeCheck,
  Timer,
  UserCheck,
  BadgeIndianRupee,
  ExternalLink,
  Linkedin,
  Github,
  Globe,
  Mail,
  Copy,
  HeartHandshake,
} from "lucide-react";
import { parseISO, formatDistanceToNow, format as formatDate } from "date-fns";

// Add these new component definitions
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-40">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="flex flex-col items-center justify-center h-40">
    <AlertTriangle className="h-8 w-8 text-red-500 mb-2" />
    <p className="text-gray-600">{message}</p>
  </div>
);
const DonationHistory = ({ donations }) => {
  if (!donations.length) {
    return (
      <EmptyState
        icon={HeartHandshake}
        title="No Donations Yet"
        description="You haven't made any donations yet. Support a campaign to get started!"
        actionLabel="Browse Campaigns"
        actionUrl="/campaigns"
      />
    );
  }

  return (
    <div className="space-y-6">
      {donations.map((donation) => (
        <div key={donation._id} className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {donation.campaignTitle}
              </h3>
              <p className="text-sm text-gray-500">{donation.college}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                donation.status
              )}`}
            >
              {donation.category}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex items-center text-gray-600">
              <DollarSign className="h-4 w-4 mr-2" />
              <span>
                {donation.donation.type === "money"
                  ? `₹${donation.donation.amount.toLocaleString()}`
                  : donation.donation.type}
              </span>
            </div>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              <span>{formatDateTime(donation.createdAt)}</span>
            </div>
          </div>

          {donation.donation.type !== "money" && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                {donation.donation.type === "skills"
                  ? donation.donation.skills.skill
                  : donation.donation.resources[0]?.description}
              </p>
            </div>
          )}
        </div>
      ))}

      {donations.length === 0 && (
        <EmptyState
          icon={HeartHandshake}
          title="No Donations Yet"
          description="You haven't made any donations yet. Support a campaign to get started!"
          actionLabel="Browse Campaigns"
          actionUrl="/campaigns"
        />
      )}
    </div>
  );
};

// Utility functions
const formatSalary = (min, max, isNegotiable) => {
  const formatNumber = (num) => (num / 100000).toFixed(1) + "L";
  return `₹${formatNumber(min)} - ${formatNumber(max)}${
    isNegotiable ? " (Negotiable)" : ""
  }`;
};

const formatDateTime = (dateString) => {
  try {
    const date = parseISO(dateString);
    return formatDate(date, "MMM d, yyyy h:mm a");
  } catch (error) {
    return dateString;
  }
};

const formatTimeAgo = (dateString) => {
  try {
    const date = parseISO(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return dateString;
  }
};

const getStatusColor = (status) => {
  const colors = {
    active: "bg-green-100 text-green-800",
    applied: "bg-blue-100 text-blue-800",
    accepted: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    pending: "bg-yellow-100 text-yellow-800",
    draft: "bg-gray-100 text-gray-800",
    published: "bg-green-100 text-green-800",
    completed: "bg-blue-100 text-blue-800",
    cancelled: "bg-red-100 text-red-800",
    "in-progress": "bg-blue-100 text-blue-800",
    upcoming: "bg-purple-100 text-purple-800",
    closed: "bg-gray-100 text-gray-800",
  };
  return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
};

// Activity Tab Card Component
const ActivityTab = ({ icon: Icon, label, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center p-4 rounded-xl transition-all duration-200 ${
      isActive
        ? "bg-blue-50 border-blue-200 shadow-sm"
        : "bg-white hover:bg-gray-50 border-gray-200"
    } border w-full`}
  >
    <div
      className={`p-2 rounded-lg ${isActive ? "bg-blue-100" : "bg-gray-100"}`}
    >
      <Icon
        className={`h-5 w-5 ${isActive ? "text-blue-600" : "text-gray-500"}`}
      />
    </div>
    <div className="ml-3 text-left">
      <p className={`text-sm ${isActive ? "text-blue-600" : "text-gray-500"}`}>
        {label}
      </p>
      <p
        className={`text-lg font-semibold ${
          isActive ? "text-blue-700" : "text-gray-700"
        }`}
      >
        {count}
      </p>
    </div>
    <ChevronRight
      className={`ml-auto h-5 w-5 ${
        isActive ? "text-blue-500" : "text-gray-400"
      }`}
    />
  </button>
);

// Job Card Component with improved UI
const JobCard = ({ job }) => (
  <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200">
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
            {job.jobDetails.title}
          </h3>
          <div className="mt-1 flex items-center text-gray-600">
            <Building className="h-4 w-4 mr-1" />
            <span>{job.company.name}</span>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
            job.status
          )}`}
        >
          {job.jobDetails.type}
        </span>
      </div>

      {/* Job Details Grid */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="flex items-center text-gray-600">
          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-sm">{job.company.location}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-sm">
            {job.jobDetails.experience.min}-{job.jobDetails.experience.max}{" "}
            years
          </span>
        </div>
        <div className="flex items-center text-gray-600">
          <DollarSign className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-sm">
            {formatSalary(
              job.jobDetails.salary.min,
              job.jobDetails.salary.max,
              job.jobDetails.salary.isNegotiable
            )}
          </span>
        </div>
        <div className="flex items-center text-gray-600">
          <Users className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-sm">
            {job.applications?.length || 0} applications
          </span>
        </div>
      </div>

      {/* Skills */}
      {job.jobDetails.skills?.length > 0 && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2">
            {job.jobDetails.skills.map((skill, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            Posted {formatTimeAgo(job.createdAt)}
          </div>
          <Link
            to={`/jobs/${job._id}`}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
          >
            View Details
            <ExternalLink className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Application Card Component with improved UI
const ApplicationCard = ({ application }) => {
  const { job, application: appDetails } = application;

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
              {job.jobDetails.title}
            </h3>
            <div className="flex items-center text-gray-600 mt-1">
              <Building className="h-4 w-4 mr-1" />
              <span>{job.company.name}</span>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
              appDetails.status
            )}`}
          >
            {appDetails.status}
          </span>
        </div>

        {/* Application Details */}
        <div className="mt-4 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Applied On</div>
              <div className="flex items-center text-sm text-gray-700">
                <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                {formatDateTime(appDetails.appliedAt)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">Location</div>
              <div className="flex items-center text-sm text-gray-700">
                <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                {job.company.location}
              </div>
            </div>
          </div>

          {/* Referral Section */}
          {appDetails.referrer && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Referral Details
              </h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {appDetails.referrer.personalInfo?.avatar ? (
                    <img
                      src={appDetails.referrer.personalInfo.avatar}
                      alt={`${appDetails.referrer.firstName}'s avatar`}
                      className="h-10 w-10 rounded-full"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {appDetails.referrer.firstName}{" "}
                      {appDetails.referrer.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {appDetails.referrer.designation} at {job.company.name}
                    </p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    appDetails.referralStatus
                  )}`}
                >
                  {appDetails.referralStatus === "accepted" && (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  {appDetails.referralStatus === "rejected" && (
                    <XCircle className="h-3 w-3 mr-1" />
                  )}
                  {appDetails.referralStatus}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Last updated {formatTimeAgo(appDetails.updatedAt)}
            </span>
            <Link
              to={`/applications/${appDetails._id}`}
              className="inline-flex items-center px-3 py-1.5 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
            >
              View Application
              <ExternalLink className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200">
      {event.images?.[0] && (
        <div className="relative h-48 rounded-t-xl overflow-hidden">
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>
      )}

      <div className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {event.title}
            </h3>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getStatusColor(
                event.status
              )}`}
            >
              {event.status}
            </span>
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 line-clamp-2">
          {event.description}
        </p>

        <div className="mt-4 space-y-3">
          <div className="flex items-center text-gray-600">
            <MapPin className="h-4 w-4 mr-2" />
            <span className="text-sm">
              {event.venue.isVirtual
                ? "Virtual Event"
                : `${event.venue.name}, ${event.venue.city}`}
            </span>
          </div>

          <div className="flex items-center text-gray-600">
            <Calendar className="h-4 w-4 mr-2" />
            <span className="text-sm">
              {formatDateTime(event.schedule.startDate)}
            </span>
          </div>

          <div className="flex items-center text-gray-600">
            <Users className="h-4 w-4 mr-2" />
            <span className="text-sm">
              {event.capacity.remaining} spots remaining
            </span>
          </div>

          {event.registration.fee.amount > 0 && (
            <div className="flex items-center text-gray-600">
              <BadgeIndianRupee className="h-4 w-4 mr-2" />
              <span className="text-sm">
                Registration Fee: ₹{event.registration.fee.amount}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="flex items-center text-sm text-gray-500">
              <Timer className="h-4 w-4 mr-1" />
              {formatTimeAgo(event.createdAt)}
            </span>
            <Link
              to={`/events/${event._id}`}
              className="inline-flex items-center text-blue-600 hover:text-blue-700"
            >
              View Event
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {event.status === "rejected" && event.approvalDetails?.comment && (
        <div className="p-4 bg-red-50 rounded-b-xl">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Rejection Reason:
              </p>
              <p className="text-sm text-red-700">
                {event.approvalDetails.comment}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const CampaignCard = ({ campaign }) => {
  const progress = Math.min(
    ((campaign.financials?.raisedAmount || 0) /
      (campaign.financials?.targetAmount || 1)) *
      100,
    100
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200">
      {campaign.images?.[0]?.url && (
        <div className="relative h-48 rounded-t-xl overflow-hidden">
          <img
            src={campaign.images[0].url}
            alt={campaign.images[0].caption || campaign.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>
      )}

      <div className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {campaign.title}
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-2">
              {campaign.category || "General"}
            </span>
          </div>
        </div>

        <p className="mt-3 text-sm text-gray-600 line-clamp-2">
          {campaign.description?.short || campaign.description?.detailed}
        </p>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-gray-900">
              ₹{(campaign.financials?.raisedAmount || 0).toLocaleString()}
            </span>
            <span className="text-gray-500">
              of ₹{(campaign.financials?.targetAmount || 0).toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-gray-500 text-right">
            {progress.toFixed(1)}% raised
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {campaign.timeline && (
            <>
              <div className="flex justify-between text-sm">
                <div className="flex items-center text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>
                    Start: {formatDateTime(campaign.timeline.startDate)}
                  </span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>End: {formatDateTime(campaign.timeline.endDate)}</span>
                </div>
              </div>
            </>
          )}

          {campaign.donations && (
            <div className="flex items-center text-gray-600">
              <Users className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {campaign.donations.length} donations received
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="flex items-center text-sm text-gray-500">
              <Timer className="h-4 w-4 mr-1" />
              {formatTimeAgo(campaign.createdAt)}
            </span>
            <Link
              to={`/campaigns/${campaign._id}`}
              className="inline-flex items-center text-blue-600 hover:text-blue-700"
            >
              View Campaign
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {campaign.status === "rejected" && campaign.approvalDetails?.comment && (
        <div className="p-4 bg-red-50 rounded-b-xl">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Rejection Reason:
              </p>
              <p className="text-sm text-red-700">
                {campaign.approvalDetails.comment}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SocialButton = ({ href, icon: Icon, label, className }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium 
    transition-all duration-200 ${className}`}
  >
    <Icon className="h-4 w-4 mr-2" />
    {label}
  </a>
);

const ReferralRequestCard = ({ job, requests, onAccept, onDecline }) => {
  const copyReferralDetails = (request) => {
    const details = `
Candidate Details:
Name: ${request.alumni.firstName} ${request.alumni.lastName}
Email: ${request.alumni.personalInfo?.alternateEmail || "N/A"}
Contact: ${request.alumni.personalInfo?.contactNumber || "N/A"}

Professional Links:
LinkedIn: ${request.alumni.socialLinks?.linkedin || "N/A"}
GitHub: ${request.alumni.socialLinks?.github || "N/A"}
Resume: ${request.alumni.socialLinks?.portfolio || "N/A"}

Current Role: ${
      request.alumni.professionalInfo?.experiences?.find((exp) => exp.isCurrent)
        ?.title || "N/A"
    }
Current Company: ${
      request.alumni.professionalInfo?.experiences?.find((exp) => exp.isCurrent)
        ?.company || "N/A"
    }

Skills: ${request.alumni.professionalInfo?.skills?.join(", ") || "N/A"}
    `.trim();

    navigator.clipboard.writeText(details);
    alert("Referral details copied to clipboard!");
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all duration-200">
      <div className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {job.jobDetails.title}
            </h3>
            <div className="flex items-center text-gray-600 mt-1">
              <Building className="h-4 w-4 mr-1" />
              <span>{job.company.name}</span>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
              job.status
            )}`}
          >
            {job.jobDetails.type}
          </span>
        </div>

        <div className="mt-6 space-y-6">
          {requests.map((request) => (
            <div key={request._id} className="relative pl-8 pb-6 last:pb-0">
              <div className="absolute left-0 top-2">
                <div className="w-4 h-4 rounded-full bg-blue-100 border-2 border-blue-500"></div>
                {requests.indexOf(request) !== requests.length - 1 && (
                  <div className="absolute top-4 left-2 w-0.5 h-full -translate-x-1/2 bg-gray-200"></div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-4 w-full">
                    <Link to={`/alumni/${request.alumni._id}`}>
                      <div className="flex items-center space-x-2">
                        {request.alumni.personalInfo?.avatar ? (
                          <img
                            src={request.alumni.personalInfo.avatar}
                            alt={`${request.alumni.firstName}'s avatar`}
                            className="h-10 w-10 rounded-full border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm">
                            <User className="h-5 w-5 text-blue-500" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {request.alumni.firstName} {request.alumni.lastName}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Requested {formatTimeAgo(request.appliedAt)}
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* Contact Section */}
                    <div className="bg-white rounded-lg p-4 space-y-4 border border-gray-100">
                      {/* Email */}
                      {request.alumni.personalInfo?.alternateEmail && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <div className="bg-blue-50 p-2 rounded-lg">
                            <Mail className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="text-sm">
                            {request.alumni.personalInfo.alternateEmail}
                          </span>
                        </div>
                      )}

                      {/* Social Links */}
                      <div className="flex flex-wrap gap-2">
                        {request.alumni.socialLinks?.linkedin && (
                          <SocialButton
                            href={request.alumni.socialLinks.linkedin}
                            icon={Linkedin}
                            label="LinkedIn"
                            className="bg-[#0A66C2] text-white hover:bg-[#004182]"
                          />
                        )}
                        {request.alumni.socialLinks?.github && (
                          <SocialButton
                            href={request.alumni.socialLinks.github}
                            icon={Github}
                            label="GitHub"
                            className="bg-gray-900 text-white hover:bg-gray-700"
                          />
                        )}
                        {request.alumni.socialLinks?.portfolio && (
                          <SocialButton
                            href={request.alumni.socialLinks.portfolio}
                            icon={FileText}
                            label="Resume"
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                          />
                        )}
                      </div>

                      {/* Copy Button */}
                      <button
                        onClick={() => copyReferralDetails(request)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 
                        bg-white border border-gray-300 rounded-lg hover:bg-gray-50 
                        hover:border-gray-400 transition-all duration-200 shadow-sm"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy All Details
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => onAccept(job._id, request._id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                      text-green-700 bg-green-100 hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Accept
                    </button>
                    <button
                      onClick={() => onDecline(job._id, request._id)}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                      text-red-700 bg-red-100 hover:bg-red-200 transition-colors"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Decline
                    </button>
                  </div>
                </div>

                {request.message && (
                  <div className="mt-3 text-sm text-gray-600 bg-white rounded-lg p-3 border border-gray-100">
                    {request.message}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// EmptyState component for reusability
export const EmptyState = ({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionUrl,
}) => (
  <div className="text-center py-12">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
      <Icon className="h-8 w-8 text-gray-400" />
    </div>
    <h3 className="mt-4 text-lg font-medium text-gray-900">{title}</h3>
    <p className="mt-2 text-gray-500">{description}</p>
    {actionLabel && actionUrl && (
      <Link
        to={actionUrl}
        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
      >
        {actionLabel}
        <ChevronRight className="ml-2 h-4 w-4" />
      </Link>
    )}
  </div>
);

// Utility components
export const StatusBadge = ({ status, className = "" }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
      status
    )} ${className}`}
  >
    {status}
  </span>
);

export const ProfilePreview = ({ user }) => (
  <div className="flex items-center space-x-3">
    {user.personalInfo?.avatar ? (
      <img
        src={user.personalInfo.avatar}
        alt={user.firstName}
        className="h-10 w-10 rounded-full"
      />
    ) : (
      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
        <User className="h-5 w-5 text-blue-600" />
      </div>
    )}
    <div>
      <p className="text-sm font-medium text-gray-900">
        {user.firstName} {user.lastName}
      </p>
      <p className="text-xs text-gray-500">
        {user.department} • {user.college?.name}
      </p>
    </div>
  </div>
);

// Loading states
export const CardSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
    </div>
  </div>
);

export const LoadingGrid = ({ count = 4 }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {Array(count)
      .fill(0)
      .map((_, i) => (
        <CardSkeleton key={i} />
      ))}
  </div>
);

// Error state
export const ErrorState = ({ message, onRetry }) => (
  <div className="text-center py-12">
    <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
    <h3 className="mt-4 text-lg font-medium text-gray-900">
      Error Loading Data
    </h3>
    <p className="mt-2 text-gray-500">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
      >
        Try Again
      </button>
    )}
  </div>
);

// Main Component
const ProfileActivities = () => {
  const [activeTab, setActiveTab] = useState("jobs");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    date: "all",
    type: "all",
  });
  

  const [postedJobs, setPostedJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [referralRequests, setReferralRequests] = useState([]);
  const [donations, setDonations] = useState([]);

 // Individual loading states
 const [sectionLoading, setSectionLoading] = useState({
  jobs: false,
  applications: false,
  events: false,
  campaigns: false,
  referrals: false,
  donations: false
});

// Individual error states
const [sectionErrors, setSectionErrors] = useState({
  jobs: null,
  applications: null,
  events: null,
  campaigns: null,
  referrals: null,
  donations: null
});

const fetchSection = async (section) => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const baseUrl = `${process.env.REACT_APP_BASE_URL}/api`;

  setSectionLoading(prev => ({ ...prev, [section]: true }));
  setSectionErrors(prev => ({ ...prev, [section]: null }));

  try {
    let response;
    switch (section) {
      case 'jobs':
        response = await axios.get(`${baseUrl}/jobs/alumni/posted`, { headers });
        setPostedJobs(response.data);
        break;
      case 'applications':
        response = await axios.get(`${baseUrl}/jobs/alumni/applications`, { headers });
        setAppliedJobs(response.data);
        break;
      case 'events':
        response = await axios.get(`${baseUrl}/events/alumni/organized`, { headers });
        setEvents(response.data);
        break;
      case 'campaigns':
        response = await axios.get(`${baseUrl}/campaigns/alumni/organized`, { headers });
        setCampaigns(response.data);
        break;
      case 'referrals':
        response = await axios.get(`${baseUrl}/jobs/alumni/referral-requests`, { headers });
        setReferralRequests(response.data);
        break;
      case 'donations':
        response = await axios.get(`${baseUrl}/campaigns/alumni/donations`, { headers });
        setDonations(response.data);
        break;
      default:
        break;
    }
  } catch (err) {
    setSectionErrors(prev => ({
      ...prev,
      [section]: err.response?.data?.message || 'Error fetching data'
    }));
  } finally {
    setSectionLoading(prev => ({ ...prev, [section]: false }));
  }
};

// Fetch data when tab changes
useEffect(() => {
  if (activeTab) {
    fetchSection(activeTab);
  }
}, [activeTab]);

  

  useEffect(() => {
    if (activeTab === "referrals") {
      fetchReferralRequests();
    }
  }, [activeTab]);

  const fetchReferralRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/jobs/alumni/referral-requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReferralRequests(response.data);
    } catch (err) {
      console.error("Error fetching referral requests:", err);
    }
  };

  const handleReferralResponse = async (jobId, applicationId, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/jobs/${jobId}/referral-response`,
        { applicationId, status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchReferralRequests(); // Refresh the list after response
    } catch (err) {
      console.error("Error updating referral status:", err);
    }
  };



  const activities = [
    {
      id: "jobs",
      label: "Posted Jobs",
      icon: Briefcase,
      count: postedJobs.length,
    },
    {
      id: "applications",
      label: "Applications",
      icon: FileText,
      count: appliedJobs.length,
    },
    {
      id: "referrals",
      label: "Referral Requests",
      icon: UserCheck,
      count: referralRequests.length,
    },
    {
      id: "events",
      label: "Events",
      icon: Calendar,
      count: events.length,
    },
    {
      id: "campaigns",
      label: "Campaigns",
      icon: Target,
      count: campaigns.length,
    },
    {
      id: "donations",
      label: "My Donations",
      icon: HeartHandshake,
      count: donations.length
    }
  ];

  const filterData = (items) => {
    if (!Array.isArray(items)) return [];

    return items.filter((item) => {
      const title =
        item.jobDetails?.title ||
        item.title ||
        item.job?.jobDetails?.title ||
        "";

      const status = item.status || item.job?.status || "unknown";

      const type =
        item.jobDetails?.type ||
        item.type ||
        item.job?.jobDetails?.type ||
        "unknown";

      const date = new Date(item.createdAt || item.appliedAt || new Date());
      const now = new Date();
      const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      const matchesSearch =
        !searchTerm || title.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        filters.status === "all" ||
        status.toLowerCase() === filters.status.toLowerCase();

      const matchesType =
        filters.type === "all" ||
        type.toLowerCase() === filters.type.toLowerCase();

      let matchesDate = true;
      switch (filters.date) {
        case "today":
          matchesDate = daysDiff === 0;
          break;
        case "week":
          matchesDate = daysDiff <= 7;
          break;
        case "month":
          matchesDate = daysDiff <= 30;
          break;
        default:
          matchesDate = true;
      }

      return matchesSearch && matchesStatus && matchesType && matchesDate;
    });
  };

 

 
  const renderFilters = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date
        </label>
        <select
          value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type
        </label>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          <option value="full-time">Full Time</option>
          <option value="part-time">Part Time</option>
          <option value="contract">Contract</option>
          <option value="internship">Internship</option>
        </select>
      </div>
    </div>
  );

  const renderContent = () => {
    // Show loading state for active section
    if (sectionLoading[activeTab]) {
      return <LoadingSpinner />;
    }

    // Show error state for active section
    if (sectionErrors[activeTab]) {
      return (
        <ErrorMessage 
          message={sectionErrors[activeTab]}
          onRetry={() => fetchSection(activeTab)}
        />
      );
    }

    // Handle donations tab separately
    if (activeTab === "donations") {
      return <DonationHistory donations={donations} />;
    }

    // Get the data for the current tab
    let data;
    switch (activeTab) {
      case "jobs":
        data = postedJobs;
        break;
      case "applications":
        data = appliedJobs;
        break;
      case "events":
        data = events;
        break;
      case "campaigns":
        data = campaigns;
        break;
      case "referrals":
        data = referralRequests;
        break;
      default:
        data = [];
    }

    // Apply filters
    const filteredData = filterData(data);

    if (filteredData.length === 0) {
      const currentActivity = activities.find(a => a.id === activeTab);
      return (
        <EmptyState
          icon={currentActivity?.icon || Users}
          title={`No ${currentActivity?.label || activeTab} Found`}
          description={
            searchTerm || showFilters
              ? "Try adjusting your filters"
              : `Start by adding your first ${activeTab.slice(0, -1)}`
          }
          actionLabel={!searchTerm && !showFilters ? `Add New ${activeTab.slice(0, -1)}` : null}
          actionUrl={!searchTerm && !showFilters ? `/${activeTab}/new` : null}
        />
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredData.map((item) => {
          switch (activeTab) {
            case "jobs":
              return <JobCard key={item._id} job={item} />;
            case "applications":
              return <ApplicationCard key={item._id} application={item} />;
            case "referrals":
              return (
                <ReferralRequestCard
                  key={item._id}
                  job={item.job}
                  requests={item.requests}
                  onAccept={(jobId, requestId) => handleReferralResponse(jobId, requestId, "accepted")}
                  onDecline={(jobId, requestId) => handleReferralResponse(jobId, requestId, "declined")}
                />
              );
            case "events":
              return <EventCard key={item._id} event={item} />;
            case "campaigns":
              return <CampaignCard key={item._id} campaign={item} />;
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Activity Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {activities.map((activity) => (
          <ActivityTab
            key={activity.id}
            icon={activity.icon}
            label={activity.label}
            count={
              activeTab === activity.id
                ? (activity.id === "donations"
                    ? donations.length
                    : activity.id === "jobs"
                    ? postedJobs.length
                    : activity.id === "applications"
                    ? appliedJobs.length
                    : activity.id === "events"
                    ? events.length
                    : activity.id === "campaigns"
                    ? campaigns.length
                    : referralRequests.length)
                : 0
            }
            isActive={activeTab === activity.id}
            onClick={() => setActiveTab(activity.id)}
          />
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              flex items-center px-4 py-2 rounded-lg border transition-colors
              ${
                showFilters
                  ? "bg-blue-50 text-blue-600 border-blue-200"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }
            `}
          >
            <Filter className="h-5 w-5 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && renderFilters()}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default ProfileActivities;
